import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaFutbol, FaPen, FaShieldAlt, FaUsers } from "react-icons/fa";
import {
  AssetGalleryManager,
  EmptyState,
  SchoolDocumentsManager,
  SectionCard,
  SectionCardSkeleton,
  SkeletonBlock,
  StatusPill,
} from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolCreateValues,
  buildSchoolFields,
  buildSchoolUpdateValues,
  getSchoolDocumentPermissions,
} from "pages/app/workspace/sections/forms/school-admin-section-forms";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { workspaceApi } from "pages/app/workspace/services";
import { formatPortalLabel, hasAnyPortalAction } from "utils/portal-access";
import { HeroSurfacePanel } from "pages/app/workspace/sections/styles/workspace-section.styles";
import {
  SchoolOverviewBadgeRow,
  SchoolOverviewBrandBlock,
  SchoolOverviewBrandStrip,
  SchoolOverviewBrandSwatch,
  SchoolOverviewBrandSwatchCard,
  SchoolOverviewBrandSwatchLabel,
  SchoolOverviewBrandThemeNote,
  SchoolOverviewContentStack,
  SchoolOverviewEditButton,
  SchoolOverviewEyebrowText,
  SchoolOverviewHeroEyebrow,
  SchoolOverviewHeroImage,
  SchoolOverviewHeroMeta,
  SchoolOverviewHeroRow,
  SchoolOverviewHeroTextGroup,
  SchoolOverviewHeroTitle,
  SchoolOverviewMetadataGrid,
  SchoolOverviewMetadataTile,
  SchoolOverviewSection,
  SchoolOverviewSectionHeading,
  SchoolOverviewStack,
  SchoolOverviewStatBody,
  SchoolOverviewStatCard,
  SchoolOverviewStatIcon,
  SchoolOverviewStatsGrid,
  SchoolOverviewStatValue,
  SchoolOverviewTitleText,
  SchoolsSectionGridLayout,
} from "pages/app/workspace/sections/styles/schools-section.inline.styles";

const useSchoolVaultQueries = (portalAccess: any, selectedSchoolId: string) => {
  const { canManageSchoolDocuments, canManageSchoolGallery, canVerifySchoolDocuments } =
    getSchoolDocumentPermissions(portalAccess);

  const selectedSchoolGalleryQuery = useQuery({
    queryKey: ["workspace-school-gallery", selectedSchoolId],
    queryFn: () => workspaceApi.galleryItems.listSchool(selectedSchoolId),
    enabled: Boolean(selectedSchoolId),
  });
  const selectedSchoolTeamsQuery = useQuery({
    queryKey: ["workspace-school-document-teams", selectedSchoolId],
    queryFn: () => workspaceApi.schoolTeams.list(selectedSchoolId),
    enabled: Boolean(selectedSchoolId),
  });
  const selectedSchoolPlayersQuery = useQuery({
    queryKey: ["workspace-school-document-players", selectedSchoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(selectedSchoolId),
    enabled: Boolean(selectedSchoolId),
  });
  const selectedSchoolSportsQuery = useQuery({
    queryKey: ["workspace-school-document-sports", selectedSchoolId],
    queryFn: () => workspaceApi.schoolSports.list(selectedSchoolId),
    enabled: Boolean(selectedSchoolId),
  });
  const selectedSchoolDocumentsQuery = useQuery({
    queryKey: ["workspace-school-documents", selectedSchoolId],
    queryFn: () => workspaceApi.schoolDocuments.list(selectedSchoolId),
    enabled: Boolean(selectedSchoolId),
  });

  return {
    canManageSchoolDocuments,
    canManageSchoolGallery,
    canVerifySchoolDocuments,
    selectedSchoolDocumentsQuery,
    selectedSchoolGalleryQuery,
    selectedSchoolPlayersQuery,
    selectedSchoolSportsQuery,
    selectedSchoolTeamsQuery,
  };
};

const formatSchoolLocation = (school: any) => {
  const locations = school?.school_locations || [];
  const primary =
    locations.find((location: any) => location?.primary_location) || locations[0] || null;
  const countryNames = (school?.countries || [])
    .map((country: any) => country?.name)
    .filter(Boolean)
    .join(", ");

  if (!primary) {
    return countryNames || "Location not set";
  }

  return (
    [
      primary.locality,
      primary.administrative_region?.name || primary.administrative_region_name,
      primary.country?.name,
      countryNames,
    ]
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index)
      .join(", ") || "Location not set"
  );
};

const SchoolBrandStrip: React.FC<{
  branding?: {
    accent_color?: string;
    primary_color?: string;
    secondary_color?: string;
    theme_mode?: string;
  } | null;
}> = ({ branding }) => {
  const palette = [
    { color: branding?.primary_color || "#1D4ED8", label: "Primary" },
    { color: branding?.secondary_color || "#0F172A", label: "Secondary" },
    { color: branding?.accent_color || "#F59E0B", label: "Accent" },
  ];

  return (
    <SchoolOverviewBrandBlock>
      <SchoolOverviewBrandStrip>
        {palette.map((entry) => (
          <SchoolOverviewBrandSwatchCard
            key={entry.label}
            title={`${entry.label}: ${entry.color}`}
          >
            <SchoolOverviewBrandSwatch $color={entry.color} aria-hidden="true" />
            <SchoolOverviewBrandSwatchLabel>{entry.label}</SchoolOverviewBrandSwatchLabel>
          </SchoolOverviewBrandSwatchCard>
        ))}
      </SchoolOverviewBrandStrip>
      <SchoolOverviewBrandThemeNote>
        Theme · {formatPortalLabel(branding?.theme_mode || "light")}
      </SchoolOverviewBrandThemeNote>
    </SchoolOverviewBrandBlock>
  );
};

const SchoolOverviewIdentity: React.FC<{
  canEdit: boolean;
  isDashboardLoading: boolean;
  onEdit?: () => void;
  overview?: {
    visible_players_count?: number;
    visible_sports_count?: number;
    visible_teams_count?: number;
  } | null;
  school: any;
}> = ({ canEdit, isDashboardLoading, onEdit, overview, school }) => {
  const schoolName = school?.name || "Unknown school";
  const locationLabel = useMemo(() => formatSchoolLocation(school), [school]);
  const heroMeta = useMemo(
    () =>
      [formatPortalLabel(school?.school_type || "School"), locationLabel].filter(Boolean).join(" · "),
    [locationLabel, school?.school_type],
  );

  const contactMeta = useMemo(
    () =>
      [
        ["Email", school?.email || "Not provided"],
        ["Phone", school?.phone_number || "Not provided"],
        ["Website", school?.website || "Not provided"],
        ["Slug", school?.slug || "Not set"],
        ["Type", formatPortalLabel(school?.school_type || "not set")],
        ["Location", locationLabel],
      ] as Array<[string, string]>,
    [locationLabel, school?.email, school?.phone_number, school?.school_type, school?.slug, school?.website],
  );

  const footprintStats = useMemo(
    () =>
      [
        {
          icon: <FaFutbol aria-hidden="true" size={16} />,
          label: "Sports",
          value: overview?.visible_sports_count ?? "—",
        },
        {
          icon: <FaShieldAlt aria-hidden="true" size={16} />,
          label: "Teams",
          value: overview?.visible_teams_count ?? "—",
        },
        {
          icon: <FaUsers aria-hidden="true" size={16} />,
          label: "Players",
          value: overview?.visible_players_count ?? "—",
        },
      ] as Array<{ icon: React.ReactNode; label: string; value: React.ReactNode }>,
    [overview?.visible_players_count, overview?.visible_sports_count, overview?.visible_teams_count],
  );

  return (
    <SchoolOverviewStack>
      <SectionCard
        action={
          canEdit && onEdit ? (
            <SchoolOverviewEditButton onClick={onEdit} type="button">
              <FaPen aria-hidden="true" size={12} />
              Edit school
            </SchoolOverviewEditButton>
          ) : undefined
        }
        title="School profile"
      >
        <SchoolOverviewContentStack>
          <HeroSurfacePanel>
            <SchoolOverviewHeroRow>
              <SchoolOverviewHeroImage
                alt={schoolName}
                fallbackLabel={schoolName}
                src={school?.image_url || school?.image}
                variant="school"
              />
              <SchoolOverviewHeroTextGroup>
                <SchoolOverviewHeroEyebrow>School</SchoolOverviewHeroEyebrow>
                <SchoolOverviewHeroTitle>{schoolName}</SchoolOverviewHeroTitle>
                <SchoolOverviewHeroMeta>{heroMeta}</SchoolOverviewHeroMeta>
                <SchoolOverviewBadgeRow>
                  <StatusPill value={school?.status || "unknown"} />
                </SchoolOverviewBadgeRow>
              </SchoolOverviewHeroTextGroup>
            </SchoolOverviewHeroRow>
          </HeroSurfacePanel>

          <SchoolOverviewSection>
            <SchoolOverviewSectionHeading>School colours</SchoolOverviewSectionHeading>
            <SchoolBrandStrip branding={school?.branding} />
          </SchoolOverviewSection>

          <SchoolOverviewSection>
            <SchoolOverviewSectionHeading>Footprint</SchoolOverviewSectionHeading>
            <SchoolOverviewStatsGrid aria-busy={isDashboardLoading || undefined}>
              {footprintStats.map((stat) => (
                <SchoolOverviewStatCard key={stat.label}>
                  <SchoolOverviewStatIcon>{stat.icon}</SchoolOverviewStatIcon>
                  <SchoolOverviewStatBody>
                    <SchoolOverviewEyebrowText>{stat.label}</SchoolOverviewEyebrowText>
                    <SchoolOverviewStatValue>{stat.value}</SchoolOverviewStatValue>
                  </SchoolOverviewStatBody>
                </SchoolOverviewStatCard>
              ))}
            </SchoolOverviewStatsGrid>
          </SchoolOverviewSection>

          <SchoolOverviewSection>
            <SchoolOverviewSectionHeading>Details</SchoolOverviewSectionHeading>
            <SchoolOverviewMetadataGrid>
              {contactMeta.map(([label, value]) => (
                <SchoolOverviewMetadataTile key={label}>
                  <SchoolOverviewEyebrowText>{label}</SchoolOverviewEyebrowText>
                  <SchoolOverviewTitleText>{value}</SchoolOverviewTitleText>
                </SchoolOverviewMetadataTile>
              ))}
            </SchoolOverviewMetadataGrid>
          </SchoolOverviewSection>
        </SchoolOverviewContentStack>
      </SectionCard>
    </SchoolOverviewStack>
  );
};

export const SchoolGalleryPanel: React.FC<{ portalAccess: any; selectedSchoolId: string }> = ({
  portalAccess,
  selectedSchoolId,
}) => {
  const { canManageSchoolGallery, selectedSchoolGalleryQuery } = useSchoolVaultQueries(
    portalAccess,
    selectedSchoolId,
  );

  if (!selectedSchoolId) {
    return (
      <EmptyState
        description="Choose a school from the header selector to manage gallery media."
        title="No school selected"
      />
    );
  }

  return (
    <AssetGalleryManager
      canManage={canManageSchoolGallery}
      createLabel="Add gallery asset"
      emptyDescription="School media will appear here when gallery items are added."
      invalidateKeys={[
        ["workspace-school-gallery", selectedSchoolId],
        ["workspace-schools"],
        ["dashboard-school-overview", selectedSchoolId],
      ]}
      isLoading={selectedSchoolGalleryQuery.isLoading}
      items={selectedSchoolGalleryQuery.data || []}
      onCreate={(payload) => workspaceApi.galleryItems.createSchool(selectedSchoolId, payload)}
      onRemove={(item) => workspaceApi.galleryItems.remove(String(item.id))}
      onUpdate={(item, payload) => workspaceApi.galleryItems.update(String(item.id), payload)}
      subtitle="Manage photo, video, poster, and supporting media assets tied directly to the selected school."
      title="School Gallery"
    />
  );
};

export const SchoolDocumentsPanel: React.FC<{ portalAccess: any; selectedSchoolId: string }> = ({
  portalAccess,
  selectedSchoolId,
}) => {
  const {
    canManageSchoolDocuments,
    canVerifySchoolDocuments,
    selectedSchoolDocumentsQuery,
    selectedSchoolPlayersQuery,
    selectedSchoolSportsQuery,
    selectedSchoolTeamsQuery,
  } = useSchoolVaultQueries(portalAccess, selectedSchoolId);

  if (!selectedSchoolId) {
    return (
      <EmptyState
        description="Choose a school from the header selector to open the document vault."
        title="No school selected"
      />
    );
  }

  return (
    <SchoolDocumentsManager
      canManage={canManageSchoolDocuments}
      canVerify={canVerifySchoolDocuments}
      createLabel="Upload document"
      documents={selectedSchoolDocumentsQuery.data || []}
      emptyDescription="School documents will appear here once staff uploads them."
      invalidateKeys={[
        ["workspace-school-documents", selectedSchoolId],
        ["dashboard-school-overview", selectedSchoolId],
      ]}
      isLoading={selectedSchoolDocumentsQuery.isLoading}
      onCreate={(payload) => workspaceApi.schoolDocuments.create(selectedSchoolId, payload)}
      onRemove={(document) => workspaceApi.schoolDocuments.remove(String(document.id))}
      onUpdate={(document, payload) =>
        workspaceApi.schoolDocuments.update(String(document.id), payload)
      }
      onVerify={(document) => workspaceApi.schoolDocuments.verify(String(document.id))}
      players={selectedSchoolPlayersQuery.data || []}
      sports={selectedSchoolSportsQuery.data || []}
      subtitle="Keep age checks, medical aid, scoresheets, fixture packs, scouting notes, and other verified school records in one vault."
      teams={selectedSchoolTeamsQuery.data || []}
      title="Document Vault"
    />
  );
};

export const SchoolsSection: React.FC<{
  embedded?: boolean;
  portalAccess: any;
  hideVaultHelper?: boolean;
}> = ({ embedded = false, portalAccess, hideVaultHelper = false }) => {
  const { schoolId: selectedSchoolId, schools, schoolsQuery, selectedSchool } =
    useScopedSchoolSelection({
      portalAccess,
    });
  const countriesQuery = useQuery({
    queryKey: ["workspace-school-form-countries"],
    queryFn: () => workspaceApi.countries.list(),
  });
  const regionsQuery = useQuery({
    queryKey: ["workspace-school-form-regions"],
    queryFn: () => workspaceApi.administrativeRegions.list(),
  });
  const schoolDashboardQuery = useQuery({
    queryKey: ["dashboard-school-overview", selectedSchoolId],
    queryFn: () => workspaceApi.schoolDashboards.get(selectedSchoolId),
    enabled: Boolean(selectedSchoolId),
  });
  const canCreateSchools = hasAnyPortalAction(portalAccess, ["schools.create", "schools.manage"]);
  const canEditSchools = hasAnyPortalAction(portalAccess, ["schools.update", "schools.manage"]);
  const schoolFields = buildSchoolFields({
    countries: countriesQuery.data || [],
    regions: regionsQuery.data || [],
  });

  const vaultPanels =
    !hideVaultHelper && schools.length && selectedSchoolId ? (
      <SchoolsSectionGridLayout>
        <SchoolGalleryPanel portalAccess={portalAccess} selectedSchoolId={selectedSchoolId} />
        <SchoolDocumentsPanel portalAccess={portalAccess} selectedSchoolId={selectedSchoolId} />
      </SchoolsSectionGridLayout>
    ) : null;

  return (
    <CrudSection
      buildCreateValues={buildSchoolCreateValues}
      buildUpdateValues={buildSchoolUpdateValues}
      canCreate={canCreateSchools}
      canEdit={canEditSchools}
      columns={[]}
      createLabel="Add school"
      data={selectedSchool ? [selectedSchool] : []}
      drawerTitleCreate="Add school"
      drawerTitleEdit="Edit school"
      emptyDescription="Choose a school from the header selector to open its profile."
      emptyTitle="No school selected"
      fields={schoolFields}
      helperCard={vaultPanels}
      invalidateKeys={[
        ["workspace-schools"],
        ["dashboard-schools"],
        ...(selectedSchoolId ? [["dashboard-school-overview", selectedSchoolId]] : []),
      ]}
      isLoading={schoolsQuery.isLoading}
      onCreate={(payload) => workspaceApi.schools.create(payload)}
      onUpdate={(row, payload) => workspaceApi.schools.update(row.id, payload)}
      embedded={embedded}
      showTitle={!embedded}
      renderList={({ canEdit, isLoading, onEdit }) => {
        if (isLoading) {
          return (
            <SectionCardSkeleton titleWidthClassName="w-40" withAction>
              <SchoolOverviewStack aria-label="Loading school profile" role="status">
                <HeroSurfacePanel>
                  <SchoolOverviewHeroRow>
                    <SkeletonBlock className="h-16 w-16 shrink-0 rounded-[22px] sm:h-20 sm:w-20" />
                    <SchoolOverviewHeroTextGroup>
                      <SkeletonBlock className="h-3 w-20 rounded-full" />
                      <SkeletonBlock className="mt-3 h-7 w-48 rounded-[14px]" />
                      <SkeletonBlock className="mt-2 h-4 w-64 max-w-full rounded-full" />
                    </SchoolOverviewHeroTextGroup>
                  </SchoolOverviewHeroRow>
                </HeroSurfacePanel>
              </SchoolOverviewStack>
            </SectionCardSkeleton>
          );
        }

        if (!selectedSchool) {
          return (
            <EmptyState
              description="Choose a school from the header selector to open its profile, branding, and contact details."
              title="No school selected"
            />
          );
        }

        return (
          <SchoolOverviewIdentity
            canEdit={canEdit}
            isDashboardLoading={schoolDashboardQuery.isLoading}
            onEdit={onEdit ? () => onEdit(selectedSchool) : undefined}
            overview={schoolDashboardQuery.data?.overview}
            school={selectedSchool}
          />
        );
      }}
      title="Overview"
    />
  );
};
